import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { bookingsAPI, locationsAPI } from '../../api/bookings';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Download, X, CreditCard, Clock, MapPin } from 'lucide-react';
import { formatDate, getStatusColor, getSportColor } from '../../lib/utils';
import { formatBookingReference, formatPaymentMethod, getBookingStatusLabel } from '../../utils/bookingUtils';
import DateTimeDisplay from '../../components/booking/DateTimeDisplay';
import CustomerHeader from '../../components/CustomerHeader';

// Status-aware price resolver. We use `||` (not `??`) so that a stored
// `final_total: 0.0` falls through to `estimated_total` instead of getting
// returned as 0 — that matters for legacy/admin bookings that skipped the
// new create_new_booking() path which writes gross_total/final_total.
// We still prefer estimated_total for explicitly-pre-payment statuses so
// the wording "Estimated Total" stays accurate while courts are pending.
const PRE_PAYMENT_STATUSES = new Set(['PENDING_ALLOCATION']);
const getDisplayTotal = (booking) => {
  if (!booking) return 0;
  if (PRE_PAYMENT_STATUSES.has(booking.status)) {
    return booking.estimated_total || booking.final_total || 0;
  }
  return booking.final_total || booking.estimated_total || 0;
};
const getDisplayGross = (booking) => {
  if (!booking) return 0;
  if (PRE_PAYMENT_STATUSES.has(booking.status)) {
    return booking.gross_total || booking.estimated_total || booking.final_total || 0;
  }
  return booking.gross_total || booking.final_total || booking.estimated_total || 0;
};

export default function BookingDetails() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const loadBooking = async () => {
    try {
      const data = await bookingsAPI.getBooking(bookingId);
      setBooking(data);
      
      // Load location info
      if (data.location_id) {
        try {
          const locationData = await locationsAPI.getLocation(data.location_id);
          setLocation(locationData);
        } catch (locError) {
          console.error('Failed to load location:', locError);
        }
      }
    } catch (error) {
      toast.error('Failed to load booking');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    
    setCancelling(true);
    try {
      console.log('Cancelling booking:', bookingId);
      const response = await bookingsAPI.cancelBooking(bookingId);
      console.log('Cancel response:', response);
      toast.success('Booking cancelled successfully');
      
      // Reload booking to show updated status
      await loadBooking();
    } catch (error) {
      console.error('Cancel error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to cancel booking';
      toast.error(errorMsg);
    } finally {
      setCancelling(false);
    }
  };

  const handlePayment = () => {
    navigate(`/payment/${bookingId}`);
  };

  const handleCheckPaymentStatus = async () => {
    setCheckingPayment(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(
        `${API_URL}/api/bookings/${bookingId}/payment/check-gateway-status`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // IMPORTANT: Check response.ok BEFORE parsing JSON
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || data.message || 'Failed to check payment status');
      }

      // Now safe to parse the response
      const data = await response.json();

      if (data.status === 'confirmed') {
        toast.success('Payment confirmed! Refreshing booking...');
        await loadBooking();
      } else if (data.status === 'failed') {
        toast.error(data.message || 'Payment failed or timeout');
      } else if (data.status === 'pending') {
        toast.info(data.message || 'Payment still pending');
      } else if (data.status === 'error') {
        toast.error(data.message || 'Failed to check payment status');
      }
    } catch (error) {
      console.error('Payment check error:', error);
      toast.error(error.message || 'Failed to check payment status');
    } finally {
      setCheckingPayment(false);
    }
  };

  const handleDownloadCalendar = async () => {
    try {
      const blob = await bookingsAPI.downloadCalendar(bookingId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `booking-${bookingId}.ics`;
      a.click();
      toast.success('Calendar file downloaded');
    } catch (error) {
      toast.error('Failed to download calendar');
    }
  };

  if (loading) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Booking not found</p>
      </div>
    );
  }

  return (
    <div className="mobile-container min-h-screen pb-bottom-nav" data-testid="booking-details">
      <CustomerHeader
        title="Booking Details"
        onBack={() => navigate('/bookings')}
        right={(
          <span className={`badge ${getStatusColor(booking.status)}`}>
            {getBookingStatusLabel(booking)}
          </span>
        )}
      />

      <div className="p-6 space-y-4">
        {/* Booking Info */}
        <Card className="p-6 glass-card">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase font-bold text-muted-foreground">Booking Reference</p>
                <p className="font-mono text-xl font-bold">{formatBookingReference(booking.id)}</p>
              </div>
              {booking.payment_method && booking.status === 'CONFIRMED' && (
                <div className="text-right">
                  <p className="text-xs uppercase font-bold text-muted-foreground">Payment Method</p>
                  <div className="flex items-center justify-end gap-2 mt-1">
                    <CreditCard className="w-4 h-4 text-blue-600" />
                    <p className="text-lg font-semibold text-blue-600">{formatPaymentMethod(booking.payment_method)}</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Location */}
            {location && (
              <div className="p-3 bg-primary/5 border border-primary/20 rounded-xl">
                <p className="text-xs uppercase font-bold text-muted-foreground mb-1">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  Location
                </p>
                <p className="font-bold text-lg">{location.name}</p>
                <p className="text-sm text-muted-foreground">{location.address}</p>
                {location.city && (
                  <p className="text-sm text-muted-foreground">{location.city}</p>
                )}
                {location.phone && (
                  <p className="text-sm text-muted-foreground mt-1">📞 {location.phone}</p>
                )}
              </div>
            )}
            
            <div>
              <p className="text-xs uppercase font-bold text-muted-foreground">Date & Time</p>
              <DateTimeDisplay
                date={booking.booking_date}
                timeSlot={booking.time_slot}
                duration={booking.duration}
                className="mt-1"
                testId="booking-details"
              />
              {booking.duration && booking.duration > 1 && (
                <span className="inline-block mt-2 px-2 py-0.5 bg-primary/10 text-primary rounded-full text-xs font-bold">
                  {booking.duration} hours
                </span>
              )}
            </div>
            <div>
              <p className="text-xs uppercase font-bold text-muted-foreground">Sport Requested</p>
              <div className="flex gap-2 mt-2">
                {booking.courts_requested.map((court, idx) => (
                  <span key={`${court.sport}-${idx}`} className={`sport-badge ${getSportColor(court.sport)}`}>
                    {court.sport} x{court.quantity}
                  </span>
                ))}
              </div>
            </div>
            {booking.courts_assigned && booking.courts_assigned.length > 0 && (
              <div>
                <p className="text-xs uppercase font-bold text-muted-foreground">Court Assigned</p>
                <div className="flex gap-2 mt-2">
                  {booking.courts_assigned.map((court) => (
                    <span key={`${court.court_number}-${court.sport}`} className={`sport-badge ${getSportColor(court.sport)}`}>
                      Court {court.court_number} - {court.sport}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {booking.extras && booking.extras.length > 0 && (
              <div>
                <p className="text-xs uppercase font-bold text-muted-foreground">Extras</p>
                {booking.extras.map((extra, idx) => (
                  <p key={`${extra.equipment_name}-${idx}`} className="text-sm">
                    {extra.equipment_name} x{extra.quantity} - BND {(extra.price * extra.quantity).toFixed(2)}
                  </p>
                ))}
              </div>
            )}
            {booking.special_requests && (
              <div>
                <p className="text-xs uppercase font-bold text-muted-foreground">Special Requests</p>
                <p className="text-sm">{booking.special_requests}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Total */}
        <Card className="p-6 glass-card border-accent">
          {/* Show breakdown only if there's an actual discount (gross != final) */}
          {(booking.discount_value > 0 || 
           (booking.gross_total && booking.final_total && booking.gross_total > booking.final_total)) ? (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <p className="text-sm font-medium text-muted-foreground">Gross Amount</p>
                <p className="text-xl font-bold">BND {getDisplayGross(booking).toFixed(2)}</p>
              </div>
              
              {/* Show discount if there's an actual discount amount */}
              {(booking.discount_value > 0 || (booking.gross_total && booking.final_total && booking.gross_total > booking.final_total)) && (
                <>
                  <div className="flex justify-between items-center text-red-600">
                    <div>
                      <p className="text-sm font-medium">
                        {booking.discount_value > 0 ? (
                          `Discount (${booking.discount_type === 'percentage' ? `${booking.discount_value}%` : `BND ${booking.discount_value}`})`
                        ) : (
                          // Guard division: gross_total could be 0 for comp/block rows.
                          `Discount (${booking.gross_total
                            ? (((booking.gross_total - (booking.final_total ?? 0)) / booking.gross_total) * 100).toFixed(0)
                            : 0}%)`
                        )}
                      </p>
                      {booking.discount_reason && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {booking.discount_reason}
                        </p>
                      )}
                    </div>
                    <p className="text-xl font-bold">−BND {(getDisplayGross(booking) - (booking.final_total ?? 0)).toFixed(2)}</p>
                  </div>
                  <div className="border-t pt-3"></div>
                </>
              )}
              
              <div className="flex justify-between items-center">
                <p className="text-lg font-bold uppercase">Net Amount</p>
                <p className="text-3xl font-black font-heading text-primary">BND {getDisplayTotal(booking).toFixed(2)}</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-between items-center">
              <p className="text-lg font-bold uppercase">
                {booking.status === 'PENDING_ALLOCATION' ? 'Estimated Total' : 'Total'}
              </p>
              <p className="text-3xl font-black font-heading">
                BND {getDisplayTotal(booking).toFixed(2)}
              </p>
            </div>
          )}
        </Card>

        {/* Actions */}
        <div className="space-y-3">
          {booking.status === 'AWAITING_PAYMENT' && (
            <>
              <Button
                onClick={handlePayment}
                data-testid="pay-button"
                className="w-full btn-primary h-14"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Confirm & Pay
              </Button>
              
              {booking.pocket_pay_order_id && (
                <Button
                  onClick={handleCheckPaymentStatus}
                  data-testid="check-payment-button"
                  variant="outline"
                  className="w-full h-14 border-2 border-primary text-primary hover:bg-primary hover:text-white"
                  disabled={checkingPayment}
                >
                  <Clock className="w-5 h-5 mr-2" />
                  {checkingPayment ? 'Checking...' : 'Check Payment Status'}
                </Button>
              )}
            </>
          )}

          {booking.status === 'CONFIRMED' && (
            <Button
              onClick={handleDownloadCalendar}
              data-testid="download-calendar-button"
              className="w-full btn-primary h-14"
            >
              <Download className="w-5 h-5 mr-2" />
              Add to Calendar
            </Button>
          )}

          {!['CANCELLED', 'CONFIRMED', 'EXPIRED', 'PAYMENT_FAILED'].includes(booking.status) && (
            <Button
              variant="destructive"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('Cancel button clicked!', { cancelling, bookingId });
                handleCancel();
              }}
              data-testid="cancel-booking-button"
              className="w-full h-14 rounded-full"
              disabled={cancelling}
              style={{ pointerEvents: 'auto', cursor: cancelling ? 'not-allowed' : 'pointer' }}
            >
              <X className="w-5 h-5 mr-2" />
              {cancelling ? 'Cancelling...' : 'Cancel Booking'}
            </Button>
          )}
          
          {booking.status === 'CANCELLED' && (
            <div className="p-4 bg-destructive/10 text-destructive rounded-2xl text-center" data-testid="cancelled-notice">
              <p className="font-bold">This booking has been cancelled</p>
            </div>
          )}

          {booking.status === 'EXPIRED' && (
            <div className="p-4 bg-slate-100 text-slate-600 rounded-2xl text-center" data-testid="expired-notice">
              <p className="font-bold">This booking expired and was released</p>
              <p className="text-sm mt-1">The payment window closed before payment was completed — a confirmation email was already sent. No further action is needed.</p>
            </div>
          )}

          {booking.status === 'PAYMENT_FAILED' && (
            <div className="p-4 bg-slate-100 text-slate-600 rounded-2xl text-center" data-testid="payment-failed-notice">
              <p className="font-bold">Payment for this booking failed</p>
              <p className="text-sm mt-1">The slot has been released. No further action is needed.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}